import { useQuery } from '@tanstack/react-query';
import supabaseClient from '@/api/supabaseClient';

export interface QuestionCategoryInfo {
  id: string;
  category_code: string;
  category: string;
  name: string;
  description: string | null;
  subject: string;
  paper_number: string;
}

const fetchQuestionCategories = async (): Promise<QuestionCategoryInfo[]> => {
  const { data, error } = await supabaseClient
    .from('question_categories')
    .select('id, category_code, category, name, description, subject, paper_number')
    .order('category_code', { ascending: true });

  if (error) {
    console.error('Error fetching question categories:', error);
    return [];
  }

  return data as QuestionCategoryInfo[];
};

/**
 * Hook to fetch question categories for display purposes.
 * Categories include names/labels from the database.
 */
export const useQuestionCategories = () => {
  return useQuery({
    queryKey: ['question-categories'],
    queryFn: fetchQuestionCategories,
    staleTime: 1000 * 60 * 60, // Cache for 1 hour - categories rarely change
  });
};

/**
 * Helper to get category label by category code (e.g., Q1, Q2, SectionB)
 * for a specific paper.
 */
export const getCategoryLabel = (
  categories: QuestionCategoryInfo[],
  category: string,
  subject?: string | null,
  paperNumber?: string | null
): string => {
  // Try to find exact match by subject and paper number
  const match = categories.find(
    (c) =>
      c.category === category &&
      (!subject || c.subject === subject) &&
      (!paperNumber || c.paper_number === paperNumber)
  );

  if (match) {
    return match.name;
  }

  // Fallback: find any category with matching category code
  const fallback = categories.find((c) => c.category === category);
  if (fallback) {
    return fallback.name;
  }

  // Last resort: return the raw category
  return category;
};

/**
 * Helper to build a category label map for a specific paper.
 */
export const buildCategoryLabelMap = (
  categories: QuestionCategoryInfo[],
  subject?: string | null,
  paperNumber?: string | null
): Record<string, string> => {
  const labelMap: Record<string, string> = {};

  // Filter categories for this paper
  const paperCategories = categories.filter(
    (c) =>
      (!subject || c.subject === subject) &&
      (!paperNumber || c.paper_number === paperNumber)
  );

  // Build map from category to name
  paperCategories.forEach((c) => {
    labelMap[c.category] = c.name;
  });

  return labelMap;
};

/**
 * Fetches which category_codes have practice questions available.
 * Returns an array of category_codes (e.g., ['LANG-P1-Q1', 'LANG-P1-Q2', 'LANG-P2-Q1']).
 */
const fetchAvailablePracticeCategoryCodes = async (): Promise<string[]> => {
  // Query distinct category_codes from practice-only questions that are published
  const { data, error } = await supabaseClient
    .from('questions')
    .select('category_code')
    .eq('is_practice_only', true)
    .eq('status', 'published')
    .not('category_code', 'is', null);

  if (error) {
    console.error('Error fetching available practice category codes:', error);
    return [];
  }

  // Get unique category_codes
  const uniqueCodes = new Set<string>();
  data.forEach((item) => {
    if (item.category_code) {
      uniqueCodes.add(item.category_code);
    }
  });

  return Array.from(uniqueCodes);
};

/**
 * Hook to fetch which category_codes have practice questions available.
 * Returns an array of category_codes (e.g., ['LANG-P1-Q1', 'LANG-P1-Q2']).
 */
export const useAvailablePracticeCategoryCodes = () => {
  return useQuery({
    queryKey: ['available-practice-category-codes'],
    queryFn: fetchAvailablePracticeCategoryCodes,
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });
};

export default useQuestionCategories;
